/**
 * /invites/accept?token=… — invite-acceptance page. UX §1.7 + AC-022/023.
 *
 * Behavior:
 *   - Preview the invite first (`GET /api/invitations/:token/preview`).
 *   - If not signed in, redirect to `/auth/request?return_to=/invites/accept?token=…`.
 *   - If signed in, present a confirm button → POST accept → toast → /.
 *   - Expired/used invites render an editorial "this invite has lapsed" message
 *     with a sign-in CTA.
 */
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import { useAcceptInvite, useInvitePreview } from '../../lib/queries';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../lib/store';
import { ApiError } from '../../lib/api';

export default function AcceptInviteRoute() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? undefined;
  const { isAuthenticated, isLoading } = useAuth();
  const preview = useInvitePreview(token);
  const accept = useAcceptInvite();
  const toast = useToast();
  const navigate = useNavigate();

  if (!token) {
    return (
      <EmptyState
        title="We couldn't find that invite."
        description="The invitation link is missing or malformed."
        cta={
          <Button variant="primary" onClick={() => navigate('/auth/request')}>
            Sign in to your journal
          </Button>
        }
      />
    );
  }

  if (!isLoading && !isAuthenticated) {
    const returnTo = `/invites/accept?token=${encodeURIComponent(token)}`;
    return (
      <Navigate
        to={`/auth/request?return_to=${encodeURIComponent(returnTo)}`}
        replace
      />
    );
  }

  if (preview.isPending || isLoading) {
    return (
      <div className="mx-auto max-w-md w-full pt-16 px-6">
        <Skeleton className="h-6 w-1/2 mb-4" />
        <Skeleton className="h-10 w-full mb-3" />
        <Skeleton className="h-10 w-2/3" />
      </div>
    );
  }

  if (preview.error) {
    const code =
      preview.error instanceof ApiError ? preview.error.code : 'INTERNAL';
    return (
      <EmptyState
        title="This invite has lapsed."
        description={
          code === 'EXPIRED'
            ? "It's past the expiry date. Ask the household owner to send a fresh one."
            : code === 'NOT_FOUND'
              ? "We couldn't find this invite."
              : 'Something went wrong on our side.'
        }
        cta={
          <Button variant="primary" onClick={() => navigate('/auth/request')}>
            Sign in to your journal
          </Button>
        }
      />
    );
  }

  const data = preview.data!;

  async function onAccept() {
    try {
      const r = await accept.mutateAsync({ token: token! });
      toast.success(`Joined the ${r.household_name} journal.`);
      navigate('/');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "We couldn't accept this invite.",
      );
    }
  }

  return (
    <section className="mx-auto max-w-md w-full pt-16 pb-12 px-6 text-center">
      <p className="text-xs uppercase tracking-[0.18em] text-ink-500 mb-3">
        An invitation
      </p>
      <h1 className="font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
        Join the {data.household_name} journal.
      </h1>
      <p className="mt-3 font-sans text-sm text-ink-600 leading-relaxed">
        Invited by {data.inviter_email}.
      </p>
      <div className="mt-10 flex items-center justify-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/')}>
          Not now
        </Button>
        <Button
          variant="primary"
          loading={accept.isPending}
          loadingLabel="Joining…"
          onClick={() => void onAccept()}
        >
          Join
        </Button>
      </div>
    </section>
  );
}
